<template>
  <section class="min-h-screen bg-gradient-to-br from-blue-500 to-slate-800 flex items-start md:items-center justify-center p-4 pt-12 md:pt-4">
    <div class="w-full max-w-sm">
      <div class="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-6 md:p-8 mt-16 md:mt-20">
        <!-- Avatar -->
        <div class="-mt-24 md:-mt-28 pb-4 flex justify-center">
          <img
            src="../../assets/earth.png"
            alt="iCollect Logo"
            class="w-24 h-24 md:w-28 md:h-28 rounded-full bg-white p-2 shadow-xl"
          />
        </div>

        <!-- Title -->
        <h1 class="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-1">
          iCollect Lite
        </h1>
        <h2 class="text-gray-500 text-center mb-6">Login</h2>

        <!-- Login Form -->
        <form @submit.prevent="login" class="space-y-4">
          <!-- Employee Number Field -->
          <div class="relative">
            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <i class="fas fa-id-badge"></i>
            </span>
            <input
              type="text"
              v-model="username"
              placeholder="Employee Number"
              autofocus
              autocomplete="username"
              required
              class="w-full h-12 pl-10 pr-4 text-base border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all hover:border-gray-300"
            />
          </div>

          <!-- Password Field -->
          <div class="relative">
            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <i class="fas fa-lock"></i>
            </span>
            <input
              :type="showPassword ? 'text' : 'password'"
              v-model="password"
              placeholder="Password"
              autocomplete="current-password"
              required
              class="w-full h-12 pl-10 pr-12 text-base border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all hover:border-gray-300"
            />
            <button
              type="button"
              @click.stop="showPassword = !showPassword"
              class="absolute right-0 top-0 h-full w-12 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Toggle password visibility"
            >
              <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
            </button>
          </div>

          <!-- Error Message -->
          <transition name="error-fade">
            <div
              v-if="error"
              class="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
            >
              <i class="fas fa-exclamation-circle"></i>
              {{ error }}
            </div>
          </transition>

          <!-- Login Button -->
          <button
            type="submit"
            :disabled="isPending || !username || !password"
            class="w-full h-13 mt-2 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg shadow-blue-500/30 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
          >
            <span v-if="isPending" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
            <template v-else>
              <i class="fas fa-sign-in-alt"></i>
              <span>Login</span>
            </template>
          </button>
        </form>

        <!-- Version Info -->
        <div class="mt-6 pt-4 border-t border-gray-200 text-center">
          <span class="text-xs text-gray-400">{{ versionNumber }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

export default {
  setup() {
    const username = ref("");
    const password = ref("");
    const showPassword = ref(false);
    const store = useStore();
    const isPending = ref(false);
    const error = ref(null);
    const router = useRouter();

    const login = async () => {
      const url = store.state.api + "driverLogin";
      try {
        isPending.value = true;
        error.value = null;

        const postBody = {
          employeeNumber: username.value,
          password: password.value,
        };

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(postBody),
        });

        const data = await response.json();

        if (data && data[0] === "Complete" && data[2] === 200) {
          const driverInfo = data[1];
          const userObj = {
            driverId: driverInfo.driverId,
            employeeNumber: driverInfo.employeeNumber,
            firstname: driverInfo.firstName,
            lastname: driverInfo.lastName,
            siteid: driverInfo.siteId,
          };
          console.debug("Login successful! Driver:", driverInfo.driverId);
          store.commit("SET_TOKEN", driverInfo.token);
          store.commit("SET_USER", userObj);
          // Persist the JWT for Authorization: Bearer on subsequent API calls.
          sessionStorage.setItem("nfToken", driverInfo.token || "");
          sessionStorage.setItem("user", JSON.stringify(userObj));
          router.push("/select-truck");
        } else {
          const detail = data[1]?.detail || data[1] || "Failed to login. Please check your credentials.";
          throw new Error(typeof detail === 'string' ? detail : "Failed to login. Please check your credentials.");
        }
      } catch (err) {
        isPending.value = false;
        error.value = err.message;
        console.error(err.message);
      }
    };

    return {
      username,
      password,
      login,
      showPassword,
      isPending,
      error,
      versionNumber: computed(() => store.state.versionNumber),
    };
  },
};
</script>

<style scoped>
.h-13 {
  height: 3.25rem;
}

.error-fade-enter-active,
.error-fade-leave-active {
  transition: all 0.3s ease;
}

.error-fade-enter-from,
.error-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
